import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  Annotation,
  BeatMeta,
  FlatSegment,
  RevealStep,
  Slide,
  V2Pack,
} from '../schema';
import {
  beatKindOf,
  findBeatById,
  findEntrySlideIndex,
  getBeatIdForSlide,
  pickNextSlideIndex,
} from './v2PackAccess';
import type { TtsBridge } from '../ports';
import type { QuizFeedback } from './quiz-types';

export type { QuizFeedback };

// ─── Platform injection: narration TTS bridge ────────────────────────────────
// The only platform coupling in the engine. The host injects its bridge once
// (Electron offline TTS, web hosted TTS, or null for clip-only / silent).

let activeTts: TtsBridge | null = null;

/** Inject (or clear) the host TTS bridge used for narration without a clip. */
export function setTtsBridge(bridge: TtsBridge | null): void {
  activeTts = bridge;
}

// ─── Debug tracing ───────────────────────────────────────────────────────────

let debugEnabled = false;

/** Toggle the engine's verbose `[audio]` console tracing (default off).
 *  Hosts flip it on when diagnosing narration/sync issues. */
export function setPlayerDebug(enabled: boolean): void {
  debugEnabled = enabled;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
function dbg(...args: any[]): void {
  if (debugEnabled) console.log(...args);
}
function dbgWarn(...args: any[]): void {
  if (debugEnabled) console.warn(...args);
}
/* eslint-enable @typescript-eslint/no-explicit-any */

/** Estimated time to read/speak narration text (~158 wpm), clamped [1.5s, 12s].
 *  Used to pace steps that have narration text but no playable audio. */
function estimateNarrationMs(text: string): number {
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return Math.min(12000, Math.max(1500, Math.round(words * 380)));
}

// ─── Public surface ────────────────────────────────────────────────────────

export type V2PlayerPhase =
  | 'idle'
  | 'revealing'             // walking the current slide's revealSteps
  | 'quiz_waiting'          // mcq revealed, awaiting click
  | 'quiz_reteach'          // playing a wrong-answer reteach branch
  | 'pace_waiting'          // pace_check question revealed, awaiting Yes/No
  | 'confidence_waiting'    // confidence_check prompt revealed, awaiting High/Med/Low
  | 'finished';

export interface V2PlayerState {
  pack: V2Pack | null;
  phase: V2PlayerPhase;

  currentSlide: Slide | null;
  currentSlideIndex: number;
  totalSlides: number;

  /** Element ids the current slide has progressively revealed so far. */
  revealedElementIds: Set<string>;
  /** Annotations accumulated across the current slide's played-so-far reveal steps. */
  accumulatedAnnotations: Annotation[];
  /**
   * The spotlight annotation for the step currently playing, or `null`. Set when
   * a `kind: 'spotlight'` step begins (after its optional `triggerMs`) and
   * cleared when that step ends, so the stage zooms only for the spotlight's
   * own step. The target is guaranteed on the current slide.
   */
  activeSpotlight: Annotation | null;
  /** The reveal step currently being narrated (drives 3D camera keyframe timing). */
  currentRevealStep: RevealStep | null;
  /** Index of `currentRevealStep` within the current slide (−1 = none). */
  currentRevealStepIndex: number;

  isSpeaking: boolean;
  /** True while playback is suspended mid-lesson (resumes from the same point). */
  isPaused: boolean;
  lastQuizFeedback: QuizFeedback | null;
  /** Flat reteach segments currently being played over a wrong quiz answer. */
  retechSegmentsPlaying: FlatSegment[] | null;
  /** Highlighted reteach segment currently being spoken. */
  activeRetechSegment: FlatSegment | null;
  error: string | null;

  loadPack: (pack: V2Pack) => void;
  start: () => void;
  /** Begin playback at a specific slide index. */
  startFrom: (index: number) => void;
  /** Begin playback at a specific reveal step of a slide (seek); prior steps reveal instantly. */
  startFromStep: (slideIndex: number, stepIndex: number) => void;
  /** Jump to a slide and show it statically (no playback) — for scrubbing. */
  seekTo: (index: number) => void;
  answerQuiz: (optionIndex: number) => void;
  answerPaceCheck: (proceed: boolean) => void;
  answerConfidence: (level: 'high' | 'medium' | 'low') => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
}

// ─── Internals ─────────────────────────────────────────────────────────────

const INTER_STEP_PAUSE_MS = 250;

// A spotlight step whose narration is empty (no audio.text / no clip) still
// needs to stay zoomed long enough to be seen — the zoom-in transition plus a
// readable hold — before it releases and the next spotlight zooms in.
const SPOTLIGHT_SILENT_HOLD_MS = 2500;
// After a spotlight releases, let the zoom-out animation finish before the next
// step zooms in, so consecutive spotlights read as zoom-out → zoom-in (not a
// jump straight from one target to the next).
const SPOTLIGHT_RELEASE_MS = 500;

/**
 * Speak text via the ElevenLabs IPC bridge. Never rejects; resolves silently
 * on bridge failure or abort so the lesson keeps flowing.
 */
function speak(
  text: string,
  signal: AbortSignal,
  segmentType: string = 'general',
  contentType: string = 'general',
  // Shared refs let pause()/resume() control the in-flight audio: the player
  // registers the live <audio> element here, and we honor `paused` so a
  // segment that arrives while paused doesn't auto-play.
  audioElRef?: { current: HTMLAudioElement | null },
  pausedRef?: { current: boolean },
  // Pre-generated narration clip URL (e.g. `lessonasset://…` or a hosted mp3).
  // When it's a real clip we play it; otherwise we fall back to live TTS.
  audioUrl?: string,
  // Known clip length (ms). Used to pace the step when a clip exists but can't
  // autoplay and there's no TTS fallback (e.g. a muted web preview before the
  // first user gesture) — we dwell this long instead of fast-forwarding.
  clipDurationMs?: number,
): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const trimmed = (text ?? '').trim();
    const clip = (audioUrl ?? '').trim();
    // A real clip = a non-empty src that isn't a `data:`/silent fallback.
    const hasClip = !!clip && !clip.startsWith('data:');
    dbg('[audio] speak() called', {
      segmentType,
      contentType,
      hasClip,
      clip: hasClip ? clip : null,
      textLen: trimmed.length,
      textPreview: trimmed.slice(0, 60),
      paused: pausedRef?.current ?? false,
    });
    if (!trimmed && !hasClip) {
      dbg('[audio] nothing to play (no text, no clip) → resolving immediately');
      resolve();
      return;
    }

    let audio: HTMLAudioElement | null = null;
    let removeSpoken: (() => void) | null = null;
    let removeError: (() => void) | null = null;
    let objectUrl: string | null = null;
    let settled = false;
    let dwellTimer: ReturnType<typeof setTimeout> | null = null;

    const settle = () => {
      if (settled) return;
      settled = true;
      if (dwellTimer) {
        clearTimeout(dwellTimer);
        dwellTimer = null;
      }
      dbg('[audio] settle() → playback finished/aborted', {
        segmentType,
        currentTime: audio?.currentTime,
        duration: audio?.duration,
      });
      removeSpoken?.();
      removeError?.();
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      signal.removeEventListener('abort', onAbort);
      if (audio) {
        if (audioElRef && audioElRef.current === audio) audioElRef.current = null;
        audio.onended = null;
        audio.onerror = null;
        audio.pause();
        audio.src = '';
        audio = null;
      }
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
      resolve();
    };
    const onAbort = () => settle();

    // Mount a playable <audio>, honoring pause/abort. `onFail` is invoked once
    // if the source can't load/play (used to fall back from a clip to TTS).
    const playSource = (src: string, onFail?: () => void) => {
      if (settled) return;
      let failed = false;
      const fail = () => {
        if (failed || settled) return;
        failed = true;
        dbgWarn('[audio] playSource FAILED', {
          src,
          error: audio?.error,
          willFallbackToTts: !!onFail,
        });
        if (onFail) onFail();
        else settle();
      };
      dbg('[audio] playSource() mounting <audio>', { src });
      audio = new Audio(src);
      if (audioElRef) audioElRef.current = audio;
      audio.onended = () => {
        dbg('[audio] clip onended', { src });
        settle();
      };
      audio.onerror = fail;
      // If we're paused right now, leave it queued — resume() will play it.
      if (!pausedRef?.current) {
        void audio
          .play()
          .then(() => dbg('[audio] clip play() started OK', { src }))
          .catch(fail);
      } else {
        dbg('[audio] paused — clip queued, not playing yet', { src });
      }
    };

    const startTts = () => {
      if (settled) return;
      // Clean up any failed clip element before switching to TTS.
      if (audio) {
        if (audioElRef && audioElRef.current === audio) audioElRef.current = null;
        audio.onended = null;
        audio.onerror = null;
        audio.pause();
        audio.src = '';
        audio = null;
      }
      const tts = activeTts;
      dbg('[audio] startTts()', { hasTts: !!tts, textLen: trimmed.length });
      if (!tts) {
        // No live TTS bridge (e.g. web preview). If a clip existed but couldn't
        // autoplay, pace the step by its known duration so reveals still play
        // out (muted) rather than fast-forwarding to the end; otherwise settle.
        if (hasClip && clipDurationMs && clipDurationMs > 0 && !pausedRef?.current) {
          dbgWarn('[audio] no tts + un-played clip → dwell for clip duration', { clipDurationMs });
          dwellTimer = setTimeout(settle, clipDurationMs);
        } else {
          dbgWarn('[audio] cannot TTS (no tts bridge) → settle');
          settle();
        }
        return;
      }
      if (!trimmed) {
        dbgWarn('[audio] cannot TTS (no text) → settle');
        settle();
        return;
      }
      removeSpoken = tts.onSpoken((buf) => {
        if (settled) return;
        const view =
          buf instanceof Uint8Array ? buf : new Uint8Array(buf as ArrayBufferLike);
        dbg('[audio] tts.onSpoken received buffer', { byteLength: view.byteLength });
        if (view.byteLength === 0) {
          dbgWarn('[audio] empty TTS buffer → settle');
          settle();
          return;
        }
        const ab = new ArrayBuffer(view.byteLength);
        new Uint8Array(ab).set(view);
        const blob = new Blob([ab], { type: 'audio/mpeg' });
        objectUrl = URL.createObjectURL(blob);
        audio = new Audio(objectUrl);
        if (audioElRef) audioElRef.current = audio;
        audio.onended = () => {
          dbg('[audio] tts clip onended');
          settle();
        };
        audio.onerror = () => {
          dbgWarn('[audio] tts clip onerror', { error: audio?.error });
          settle();
        };
        if (!pausedRef?.current) {
          void audio
            .play()
            .then(() => dbg('[audio] tts play() started OK'))
            .catch((err) => {
              dbgWarn('[audio] tts play() rejected', err);
              settle();
            });
        } else {
          dbg('[audio] paused — tts clip queued, not playing yet');
        }
      });
      removeError = tts.onSpeakError(() => {
        dbgWarn('[audio] tts.onSpeakError → settle');
        settle();
      });
      dbg('[audio] tts.speak() requested', { segmentType, contentType });
      tts.speak(trimmed, segmentType, contentType);
    };

    signal.addEventListener('abort', onAbort, { once: true });

    // Pre-generated clip → play it; on load/playback failure, fall back to TTS.
    if (hasClip) {
      playSource(clip, startTts);
      return;
    }
    startTts();
  });
}

function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) { resolve(); return; }
    const id = setTimeout(resolve, ms);
    signal.addEventListener('abort', () => { clearTimeout(id); resolve(); }, { once: true });
  });
}

function contentTypeFor(pack: V2Pack | null): string {
  if (!pack) return 'general';
  const subject = String(pack.subject ?? '').toLowerCase();
  if (/math|algebra|geometry|arith/.test(subject)) return 'math';
  if (/science|physics|chem|bio/.test(subject)) return 'science';
  if (/history|civics/.test(subject)) return 'history';
  if (/lang|english|hindi|grammar|literature/.test(subject)) return 'language';
  const grade = String(pack.grade ?? '').toLowerCase();
  if (/kid|class\s*[1-3]\b/.test(grade)) return 'kids';
  return 'general';
}

// ─── Hook ──────────────────────────────────────────────────────────────────

export function useSlidePackPlayer(): V2PlayerState {
  const [pack, setPack] = useState<V2Pack | null>(null);
  const [phase, setPhase] = useState<V2PlayerPhase>('idle');
  const [slideIdx, setSlideIdx] = useState(0);
  const [revealedElementIds, setRevealedElementIds] = useState<Set<string>>(new Set());
  const [accumulatedAnnotations, setAccumulatedAnnotations] = useState<Annotation[]>([]);
  const [activeSpotlight, setActiveSpotlight] = useState<Annotation | null>(null);
  // The reveal step currently being narrated — drives 3D camera keyframe timing.
  const [currentRevealStep, setCurrentRevealStep] = useState<RevealStep | null>(null);
  // Index of the current reveal step within the current slide (−1 = none). Lets
  // the host position a timeline playhead / seekbar.
  const [currentRevealStepIndex, setCurrentRevealStepIndex] = useState(-1);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [lastQuizFeedback, setLastQuizFeedback] = useState<QuizFeedback | null>(null);
  const [retechSegmentsPlaying, setRetechSegmentsPlaying] = useState<FlatSegment[] | null>(null);
  const [activeRetechSegment, setActiveRetechSegment] = useState<FlatSegment | null>(null);
  const [error, setError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const contentTypeRef = useRef<string>('general');

  // Pause/resume: `pausedRef` gates the playback loop, `audioElRef` holds the
  // in-flight <audio> so we can pause/resume it, and `resumeResolversRef` holds
  // loop continuations waiting to be released on resume.
  const pausedRef = useRef(false);
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const resumeResolversRef = useRef<Array<() => void>>([]);

  /** Block the playback loop while paused; resolves on resume or abort. */
  const waitWhilePaused = useCallback(
    (signal: AbortSignal): Promise<void> =>
      new Promise((resolve) => {
        if (!pausedRef.current || signal.aborted) { resolve(); return; }
        let done = () => {};
        done = () => {
          signal.removeEventListener('abort', done);
          resolve();
        };
        resumeResolversRef.current.push(done);
        signal.addEventListener('abort', done, { once: true });
      }),
    [],
  );

  // Resolved by the renderer when the user picks an answer / proceeds.
  const quizResolverRef = useRef<((optionIndex: number) => void) | null>(null);
  const paceResolverRef = useRef<((proceed: boolean) => void) | null>(null);
  const confidenceResolverRef = useRef<((level: 'high' | 'medium' | 'low') => void) | null>(null);

  const cleanupAbort = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    quizResolverRef.current = null;
    paceResolverRef.current = null;
    confidenceResolverRef.current = null;
  }, []);

  useEffect(() => () => cleanupAbort(), [cleanupAbort]);

  // ─── Step runner ────────────────────────────────────────────────────────

  /** Walk through one revealStep: union elementIds, push annotations, speak. */
  const runStep = useCallback(
    async (step: RevealStep, signal: AbortSignal): Promise<void> => {
      if (signal.aborted) return;
      // Hold here (without revealing the next element) while paused.
      await waitWhilePaused(signal);
      if (signal.aborted) return;
      // Mark this as the active step so the 3D camera can time its keyframes.
      setCurrentRevealStep(step);
      const ids = Array.isArray(step.elementIds) ? step.elementIds : [];
      const annotations = Array.isArray(step.annotations) ? step.annotations : [];

      if (ids.length > 0) {
        setRevealedElementIds((prev) => {
          const next = new Set(prev);
          ids.forEach((id) => next.add(id));
          return next;
        });
      }
      if (annotations.length > 0) {
        setAccumulatedAnnotations((prev) => [...prev, ...annotations]);
      }

      // Focus cue (zoom or spotlight): dim/zoom the target for THIS step only.
      // Arm after the optional `triggerMs` offset into narration; the stage does
      // the visual. Cleared at step exit below so the next step starts clean.
      const spotlight =
        annotations.find((a) => a.kind === 'zoom' || a.kind === 'spotlight') ??
        null;
      let spotlightTimer = 0;
      if (spotlight) {
        const triggerMs = Math.max(0, spotlight.triggerMs ?? 0);
        if (triggerMs === 0) {
          setActiveSpotlight(spotlight);
        } else {
          spotlightTimer = window.setTimeout(() => {
            if (!signal.aborted) setActiveSpotlight(spotlight);
          }, triggerMs);
        }
        signal.addEventListener(
          'abort',
          () => window.clearTimeout(spotlightTimer),
          { once: true },
        );
      }

      // How long this step's timed region visuals (strokes / region spotlights)
      // need on screen — so a step doesn't end before its last stroke finishes
      // drawing or its spotlight has had its moment. Defaults: a stroke draws
      // for up to ~1.6s; a spotlight holds like the silent-spotlight dwell.
      const stepEntryTs = typeof performance !== 'undefined' ? performance.now() : Date.now();
      const visualEndMs = Math.max(
        0,
        ...(step.drawStrokes ?? []).map((s) => s.endMs ?? (s.triggerMs ?? 0) + 1600),
        ...(step.spotlights ?? []).map(
          (s) => s.endMs ?? (s.triggerMs ?? 0) + SPOTLIGHT_SILENT_HOLD_MS,
        ),
      );

      const audioText = step.audio?.text?.trim();
      // Pre-generated narration clip (bundled); falls back to TTS when absent.
      const audioSrc = step.audio?.src;
      const willSpeak = !!(audioText || audioSrc);
      // Whether this step will actually produce audio: a real clip, or a TTS
      // bridge to speak the text. When NEITHER is present (e.g. a silent web
      // preview with no TTS), speak() returns instantly — so a narration-only
      // step would flash past. Hold it for an estimated reading/speaking time
      // so the pacing reads right (and camera keyframes on the step can play).
      const hasRealAudio = !!audioSrc || !!activeTts;
      if (willSpeak) {
        setIsSpeaking(true);
        // Spotlight stays zoomed for the WHOLE narration: speak() resolves only
        // when the audio finishes, and we release the zoom after it below.
        await speak(
          audioText ?? '',
          signal,
          step.label ?? 'reveal',
          contentTypeRef.current,
          audioElRef,
          pausedRef,
          audioSrc,
          step.audio?.durationMs,
        );
        setIsSpeaking(false);
        if (!hasRealAudio && audioText) {
          await delay(estimateNarrationMs(audioText), signal);
        }
        // Narration usually outlasts the visuals; when it doesn't (short clip,
        // long-tailed stroke/spotlight), hold until the visuals complete.
        const elapsed =
          (typeof performance !== 'undefined' ? performance.now() : Date.now()) - stepEntryTs;
        if (visualEndMs - elapsed > 0) await delay(visualEndMs - elapsed, signal);
      } else if (spotlight || visualEndMs > 0) {
        // Spotlight/stroke step with no narration: hold for a dwell so the
        // visuals actually play out, instead of flashing past in one frame.
        await waitWhilePaused(signal);
        await delay(Math.max(SPOTLIGHT_SILENT_HOLD_MS, visualEndMs), signal);
      }
      await delay(INTER_STEP_PAUSE_MS, signal);
      // Release the spotlight as the step ends (zoom animates back to 1×), then
      // let that zoom-out finish before the next step so consecutive spotlights
      // read as out → in rather than jumping target-to-target.
      if (spotlight) {
        window.clearTimeout(spotlightTimer);
        setActiveSpotlight(null);
        await delay(SPOTLIGHT_RELEASE_MS, signal);
      }
    },
    [waitWhilePaused],
  );

  // ─── Overlay waits ──────────────────────────────────────────────────────

  const waitForQuizAnswer = useCallback(
    (signal: AbortSignal): Promise<number | null> =>
      new Promise((resolve) => {
        if (signal.aborted) { resolve(null); return; }
        quizResolverRef.current = (i: number) => {
          quizResolverRef.current = null;
          resolve(i);
        };
        signal.addEventListener('abort', () => {
          quizResolverRef.current = null;
          resolve(null);
        }, { once: true });
      }),
    [],
  );

  const waitForPace = useCallback(
    (signal: AbortSignal): Promise<boolean | null> =>
      new Promise((resolve) => {
        if (signal.aborted) { resolve(null); return; }
        paceResolverRef.current = (proceed: boolean) => {
          paceResolverRef.current = null;
          resolve(proceed);
        };
        signal.addEventListener('abort', () => {
          paceResolverRef.current = null;
          resolve(null);
        }, { once: true });
      }),
    [],
  );

  const waitForConfidence = useCallback(
    (signal: AbortSignal): Promise<'high' | 'medium' | 'low' | null> =>
      new Promise((resolve) => {
        if (signal.aborted) { resolve(null); return; }
        confidenceResolverRef.current = (level) => {
          confidenceResolverRef.current = null;
          resolve(level);
        };
        signal.addEventListener('abort', () => {
          confidenceResolverRef.current = null;
          resolve(null);
        }, { once: true });
      }),
    [],
  );

  // ─── Per-slide kind handlers ────────────────────────────────────────────

  /** Play a flat reteach branch (board + speech segments). */
  const playRetechSegments = useCallback(
    async (segments: FlatSegment[], signal: AbortSignal): Promise<void> => {
      if (!segments?.length) return;
      setRetechSegmentsPlaying(segments);
      try {
        for (const seg of segments) {
          if (signal.aborted) return;
          // eslint-disable-next-line no-await-in-loop
          await waitWhilePaused(signal);
          if (signal.aborted) return;
          setActiveRetechSegment(seg);
          setIsSpeaking(true);
          // Play the bundled clip when present; otherwise TTS the segment's
          // `speech` (handoff §2b). Keyed off `speech`, not `audio.text`.
          // eslint-disable-next-line no-await-in-loop
          await speak(
            seg.speech ?? '',
            signal,
            seg.type ?? 'reteach',
            contentTypeRef.current,
            audioElRef,
            pausedRef,
            seg.audio?.src,
          );
          setIsSpeaking(false);
          // eslint-disable-next-line no-await-in-loop
          await delay(INTER_STEP_PAUSE_MS, signal);
        }
      } finally {
        setActiveRetechSegment(null);
        setRetechSegmentsPlaying(null);
      }
    },
    [waitWhilePaused],
  );

  const runQuizFlow = useCallback(
    async (currentPack: V2Pack, slide: Slide, signal: AbortSignal): Promise<void> => {
      const meta = (slide.beatMeta ?? {}) as BeatMeta;
      const mcqEl = slide.rows
        .flatMap((r) => r.columns)
        .flatMap((c) => c.elements)
        .find((e) => e.type === 'mcq');
      const content = (mcqEl?.content ?? {}) as {
        optionIds?: string[];
        correctOptionId?: string;
        options?: string[];
        answerIndex?: number;
      };
      const optionIds = content.optionIds ?? [];
      const correctOptionId = meta.correctOptionId ?? content.correctOptionId ?? null;

      // Retry loop: keep asking until correct (mirrors legacy quiz behavior).
      let level: 1 | 2 = 1;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (signal.aborted) return;
        setPhase('quiz_waiting');
        // eslint-disable-next-line no-await-in-loop
        const picked = await waitForQuizAnswer(signal);
        if (signal.aborted || picked === null) return;

        const optionId =
          optionIds[picked] ?? String.fromCharCode(97 + picked);
        const correct =
          correctOptionId !== null
            ? optionId === correctOptionId
            : picked === (content.answerIndex ?? -1);

        setLastQuizFeedback({ optionId, optionIndex: picked, correct });
        if (correct) {
          // Linger on the correct answer so the pressed option stays visible
          // on screen for a beat before the lesson continues.
          await delay(2500, signal);
          return;
        }

        // Wrong → show the pressed option briefly before the reteach kicks in.
        await delay(2000, signal);
        if (signal.aborted) return;

        // Wrong → play the matching reteach branch (escalate to level 2 on
        // second wrong attempt). Falls back to generalReteach if the picked
        // option has no branch.
        const branch = meta.branches?.[optionId];
        const segs: FlatSegment[] =
          (level === 1 ? branch?.reteachLevel1 : branch?.reteachLevel2)
          ?? branch?.reteachSegments
          ?? meta.generalReteach
          ?? [];
        if (segs.length > 0) {
          setPhase('quiz_reteach');
          // eslint-disable-next-line no-await-in-loop
          await playRetechSegments(segs, signal);
        }
        level = level === 1 ? 2 : 2;
      }
      // unreachable
      void currentPack;
    },
    [waitForQuizAnswer, playRetechSegments],
  );

  // ─── Top-level slide runner ─────────────────────────────────────────────

  const runFromSlide = useCallback(
    async (
      currentPack: V2Pack,
      startIndex: number,
      signal: AbortSignal,
      // On the FIRST slide of this run, skip narration/dwell for steps before
      // this index but still reveal their content instantly — i.e. "seek" to
      // that step. 0 (default) plays normally from the top.
      fromStep = 0,
    ): Promise<void> => {
      let idx = startIndex;
      let firstSlide = true;
      // The slide played just before the current one — used to "re-explain" the
      // concept when a confidence check comes back low and the beat has no
      // explicit re-explain target.
      let prevIdx = -1;
      // Cap on number of slide transitions per playback to defend against
      // accidentally-cyclic `next` routing; lessons we ship are << this.
      const MAX_TRANSITIONS = 256;
      let transitions = 0;

      while (idx >= 0 && idx < currentPack.slides.length && transitions < MAX_TRANSITIONS) {
        if (signal.aborted) return;
        const slide = currentPack.slides[idx];
        setSlideIdx(idx);
        setRevealedElementIds(new Set());
        setAccumulatedAnnotations([]);
        setActiveSpotlight(null);
        setLastQuizFeedback(null);
        setPhase('revealing');

        const steps = Array.isArray(slide.revealSteps) ? slide.revealSteps : [];
        const skipBefore = firstSlide ? Math.max(0, Math.min(fromStep, steps.length)) : 0;
        for (let i = 0; i < steps.length; i++) {
          if (signal.aborted) return;
          const step = steps[i];
          setCurrentRevealStepIndex(i);
          if (i < skipBefore) {
            // Instant catch-up so a seek lands with prior content on screen,
            // without playing audio/dwell. (endMs/word timing is ignored here —
            // we just show the accumulated state at the seek point.)
            setCurrentRevealStep(step);
            setRevealedElementIds((prev) => {
              const next = new Set(prev);
              (step.elementIds ?? []).forEach((id) => next.add(id));
              return next;
            });
            if (step.annotations?.length) {
              setAccumulatedAnnotations((prev) => [...prev, ...step.annotations!]);
            }
            continue;
          }
          // eslint-disable-next-line no-await-in-loop
          await runStep(step, signal);
        }
        firstSlide = false;
        if (signal.aborted) return;

        const kind = beatKindOf(slide);
        let outcome: 'default' | 'ifExpanded' | 'ifReteach' | 'ifSkipped' = 'default';
        // Explicit re-explain target (slide index). When set, it overrides the
        // normal `next`-based routing for this beat.
        let reexplainIdx = -1;

        if (kind === 'quiz') {
          // eslint-disable-next-line no-await-in-loop
          await runQuizFlow(currentPack, slide, signal);
          if (signal.aborted) return;
        } else if (kind === 'pace_check') {
          // If the slide didn't speak a question (no audio.text in any
          // revealStep), speak the beat-level question now so it's not silent.
          const spokeQuestion = steps.some((s) => !!s.audio?.text);
          if (!spokeQuestion) {
            const beatId = getBeatIdForSlide(slide);
            const beat = findBeatById(currentPack, beatId);
            const speech = beat?.question?.speech ?? beat?.prompt?.speech ?? '';
            if (speech) {
              setIsSpeaking(true);
              // eslint-disable-next-line no-await-in-loop
              await speak(speech, signal, 'question', contentTypeRef.current, audioElRef, pausedRef);
              setIsSpeaking(false);
            }
          }
          if (signal.aborted) return;
          setPhase('pace_waiting');
          // eslint-disable-next-line no-await-in-loop
          const proceed = await waitForPace(signal);
          if (signal.aborted || proceed === null) return;
          // Yes → continue (default). No → re-explain: prefer an authored
          // ifExpanded target, otherwise replay the previous slide. (Without
          // this fallback, an unauthored ifExpanded resolves to `default` and
          // the lesson would wrongly move on.)
          outcome = proceed ? 'default' : 'ifExpanded';
          if (!proceed) {
            const expandedIdx = pickNextSlideIndex(currentPack, slide, 'ifExpanded');
            const defaultIdx = pickNextSlideIndex(currentPack, slide, 'default');
            if (expandedIdx !== -1 && expandedIdx !== defaultIdx) {
              reexplainIdx = expandedIdx; // real re-explain target authored
            } else if (prevIdx !== -1) {
              reexplainIdx = prevIdx; // no target → re-teach the prior concept
            }
          }
        } else if (kind === 'confidence_check') {
          const spokeQuestion = steps.some((s) => !!s.audio?.text);
          if (!spokeQuestion) {
            const beatId = getBeatIdForSlide(slide);
            const beat = findBeatById(currentPack, beatId);
            const speech = beat?.prompt?.speech ?? beat?.question?.speech ?? '';
            if (speech) {
              setIsSpeaking(true);
              // eslint-disable-next-line no-await-in-loop
              await speak(speech, signal, 'question', contentTypeRef.current, audioElRef, pausedRef);
              setIsSpeaking(false);
            }
          }
          if (signal.aborted) return;
          setPhase('confidence_waiting');
          // eslint-disable-next-line no-await-in-loop
          const level = await waitForConfidence(signal);
          if (signal.aborted || level === null) return;
          // High / Medium → continue; Low → re-explain (like the pace check's
          // "No"). Prefer an explicit ifExpanded target; if the beat doesn't
          // define one (so ifExpanded would just fall back to default and the
          // lesson would wrongly continue), replay the previous slide instead.
          outcome = level === 'low' ? 'ifExpanded' : 'default';
          if (level === 'low') {
            const expandedIdx = pickNextSlideIndex(currentPack, slide, 'ifExpanded');
            const defaultIdx = pickNextSlideIndex(currentPack, slide, 'default');
            if (expandedIdx !== -1 && expandedIdx !== defaultIdx) {
              reexplainIdx = expandedIdx; // real re-explain target authored
            } else if (prevIdx !== -1) {
              reexplainIdx = prevIdx; // no target → re-teach the prior concept
            }
          }
        }
        // 'teach' | 'summary' | 'engagement_reset' → default routing.

        const nextIdx =
          reexplainIdx !== -1
            ? reexplainIdx
            : pickNextSlideIndex(currentPack, slide, outcome);
        if (nextIdx === -1) break;
        prevIdx = idx;
        idx = nextIdx;
        transitions += 1;
      }

      if (!signal.aborted) {
        setPhase('finished');
      }
    },
    [runStep, runQuizFlow, waitForPace, waitForConfidence],
  );

  // ─── Public actions ─────────────────────────────────────────────────────

  /** Begin playback at an explicit slide index (used by start + the scrubber).
   *  `fromStep` seeks within that slide — prior steps reveal instantly. */
  const startFrom = useCallback((index: number, fromStep = 0) => {
    if (!pack) { setError('No pack loaded'); return; }
    const slides = pack.slides ?? [];
    if (slides.length === 0) {
      setError('Pack has no slides');
      setPhase('idle');
      return;
    }
    const idx = Math.max(0, Math.min(index, slides.length - 1));
    cleanupAbort();
    pausedRef.current = false;
    setIsPaused(false);
    audioElRef.current = null;
    resumeResolversRef.current = [];
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    contentTypeRef.current = contentTypeFor(pack);
    setError(null);
    setRevealedElementIds(new Set());
    setAccumulatedAnnotations([]);
    setCurrentRevealStepIndex(-1);
    setLastQuizFeedback(null);
    setRetechSegmentsPlaying(null);
    setActiveRetechSegment(null);
    setActiveSpotlight(null);
    setSlideIdx(idx);
    setPhase('revealing');
    void runFromSlide(pack, idx, ctrl.signal, fromStep).catch((err) => {
      if (ctrl.signal.aborted) return;
      setError(err instanceof Error ? err.message : String(err));
      setPhase('idle');
    });
  }, [pack, cleanupAbort, runFromSlide]);

  /** Begin playback at a specific reveal step of a slide (seek). */
  const startFromStep = useCallback((slideIndex: number, stepIndex: number) => {
    startFrom(slideIndex, stepIndex);
  }, [startFrom]);

  const start = useCallback(() => {
    if (!pack) { setError('No pack loaded'); return; }
    const entry = findEntrySlideIndex(pack);
    startFrom(entry === -1 ? 0 : entry);
  }, [pack, startFrom]);

  /** Stop playback and show a slide statically (full preview) — for scrubbing. */
  const seekTo = useCallback((index: number) => {
    if (!pack) return;
    const slides = pack.slides ?? [];
    if (slides.length === 0) return;
    const idx = Math.max(0, Math.min(index, slides.length - 1));
    cleanupAbort();
    pausedRef.current = false;
    setIsPaused(false);
    audioElRef.current = null;
    resumeResolversRef.current = [];
    setSlideIdx(idx);
    setPhase('idle');
    setIsSpeaking(false);
    setRevealedElementIds(new Set());
    setAccumulatedAnnotations([]);
    setLastQuizFeedback(null);
    setRetechSegmentsPlaying(null);
    setActiveRetechSegment(null);
    setActiveSpotlight(null);
  }, [pack, cleanupAbort]);

  const loadPack = useCallback((next: V2Pack) => {
    cleanupAbort();
    pausedRef.current = false;
    setIsPaused(false);
    audioElRef.current = null;
    resumeResolversRef.current = [];
    setPack(next);
    setPhase('idle');
    setSlideIdx(findEntrySlideIndex(next));
    setRevealedElementIds(new Set());
    setAccumulatedAnnotations([]);
    setIsSpeaking(false);
    setLastQuizFeedback(null);
    setRetechSegmentsPlaying(null);
    setActiveRetechSegment(null);
    setActiveSpotlight(null);
    setError(null);
  }, [cleanupAbort]);

  const answerQuiz = useCallback((optionIndex: number) => {
    if (phase !== 'quiz_waiting') return;
    quizResolverRef.current?.(optionIndex);
  }, [phase]);

  const answerPaceCheck = useCallback((proceed: boolean) => {
    if (phase !== 'pace_waiting') return;
    paceResolverRef.current?.(proceed);
  }, [phase]);

  const answerConfidence = useCallback((level: 'high' | 'medium' | 'low') => {
    if (phase !== 'confidence_waiting') return;
    confidenceResolverRef.current?.(level);
  }, [phase]);

  /** Suspend playback in place; the loop and current audio freeze. */
  const pause = useCallback(() => {
    if (!abortRef.current || pausedRef.current) return;
    pausedRef.current = true;
    setIsPaused(true);
    audioElRef.current?.pause();
    setIsSpeaking(false);
  }, []);

  /** Continue from exactly where pause() stopped. */
  const resume = useCallback(() => {
    if (!abortRef.current || !pausedRef.current) return;
    pausedRef.current = false;
    setIsPaused(false);
    // Resume the segment that was playing (or was queued while paused).
    const audio = audioElRef.current;
    if (audio) {
      setIsSpeaking(true);
      void audio.play().catch(() => {});
    }
    // Release any loop continuations waiting on the pause gate.
    const waiters = resumeResolversRef.current;
    resumeResolversRef.current = [];
    waiters.forEach((w) => w());
  }, []);

  const stop = useCallback(() => {
    cleanupAbort();
    pausedRef.current = false;
    setIsPaused(false);
    audioElRef.current = null;
    resumeResolversRef.current = [];
    setPhase('idle');
    setIsSpeaking(false);
    setRetechSegmentsPlaying(null);
    setActiveRetechSegment(null);
    setActiveSpotlight(null);
  }, [cleanupAbort]);

  const currentSlide: Slide | null = pack?.slides[slideIdx] ?? null;

  return {
    pack,
    phase,
    currentSlide,
    currentSlideIndex: slideIdx,
    totalSlides: pack?.slides.length ?? 0,
    revealedElementIds,
    accumulatedAnnotations,
    activeSpotlight,
    currentRevealStep,
    currentRevealStepIndex,
    isSpeaking,
    isPaused,
    lastQuizFeedback,
    retechSegmentsPlaying,
    activeRetechSegment,
    error,
    loadPack,
    start,
    startFrom,
    startFromStep,
    seekTo,
    answerQuiz,
    answerPaceCheck,
    answerConfidence,
    pause,
    resume,
    stop,
  };
}
